<template>
  <li class="note-link" :class="classNote" v-show="isNoteShowable">
    <span class="child-notice-indicator" v-if="!note.root"></span>
    <router-link :to="{ ...queryParams() }" class="serif">
      <slot v-bind:note="note">
        <div v-if="useNoteLabel && isLabel">{{ note.label }}</div>
        <div v-else>{{ configLabel }}</div>
      </slot>
      <div v-if="note.status">({{ status }})</div>
      <Attr :value="note.extract">
        <p slot-scope="$" class="extract">{{ $.value | supplantLink | removeMD | truncate }}</p>
      </Attr>
      <div>
        <span class="notice-button" v-b-tooltip.right :title="tooltipText">
          <i
            :class="[
              'icon-notice',
              { 'notice-open': !isNoteOpen, 'notice-close': isNoteOpen, 'notice-highlight': highlight },
            ]"
          ></i>
        </span>
      </div>
    </router-link>
    <span class="top-right corner-cut"></span>
  </li>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import Attr from './Attr.vue';
import { partialStrings } from 'config';
import textNoteStatusMixin from 'mixins/textNoteStatusMixin.js';
import removeMD from 'remove-markdown';
import FilterBuilder from 'lib/filterBuilder';

export default {
  name: 'TextNoteExtract',
  props: ['note', 'type', 'highlight'],
  mixins: [textNoteStatusMixin],
  components: {
    Attr,
  },
  filters: {
    removeMD: function (value) {
      return removeMD(value);
    },
  },
  computed: {
    ...mapState('auth', ['currentUser', 'isAuthenticated']),
    ...mapGetters('params', ['locationFilters']),
    ...mapState('route', ['query', 'params']),
    configLabel() {
      return this.partial.text_notes[`${this.type}Label`];
    },
    queryNote() {
      return this.params.note;
    },
    tooltipText() {
      return this.isNoteOpen ? this.$gettext('Fermer la notice') : this.$gettext('Afficher la notice');
    },
    classNote() {
      return {
        'notice-unfolded': this.isNoteOpen,
        'child-notice': !this.note.root,
      };
    },
    isNoteOpen() {
      const parsedId = this.queryNote?.split('-').reverse()[0];

      if (!this.queryNote) {
        return false;
      }

      if (typeof this.note === 'string') {
        return parsedId == this.note;
      }

      if (typeof this.note === 'object') {
        return parsedId == this.note.id;
      }

      return false;
    },
    isNoteShowable() {
      const { status } = this.note;
      const read_unpublished = this.isAuthenticated ? this.currentUser.abilities.text_note.read_unpublished : null;
      return !status || read_unpublished;
    },
    isLabel() {
      const { label } = this.note;
      return label && label !== '';
    },
    useNoteLabel() {
      const {
        text_notes: { use_label_from_data },
      } = this.partial;
      return use_label_from_data && use_label_from_data === 'yes';
    },
    status() {
      return this.textNoteStatus(this.note);
    },
  },
  methods: {
    queryParams() {
      if (this.$route.name === 'Movements') {
        const parsedId = this.query.note?.split('-').reverse()[0];
        const filters = FilterBuilder.toURLParam(this.locationFilters);
        const { note, ...rest } = this.$route.query;
        let query = {};

        if (this.note.id == parsedId) {
          query = Object.assign({ note: null }, rest);
        } else {
          const noteId = this.note.slug ?? this.note.id;
          query = Object.assign({ note: noteId }, rest);
        }
        return {
          query,
          ...(this.locationFilters.length && { params: { filters } }),
        };
      } else {
        const parsedId = this.queryNote?.split('-').reverse()[0];
        if (this.note.id == parsedId) {
          const query = Object.assign({}, this.$route.query);
          return { name: 'artwork', query };
        } else {
          const query = Object.assign({}, this.$route.query);
          const noteId = this.note.slug ?? this.note.id;
          return { name: 'ArtworkNote', query, params: { note: noteId } };
        }
      }
    },
  },
  data() {
    return {
      partial: partialStrings,
    };
  },
};
</script>
<style lang="less">
.serif {
  color: #1d1d1d;
  font-family: serif;
}

.note-link {
  position: relative;
  padding: 0 !important;
  min-height: 57px;
  & > a {
    width: 100%;
    display: inline-block;
    padding: 10px 20px 10px 10px;
  }
  &:hover {
    background-color: @chalky;
    .notice-open {
      font-size: 30px;
    }
    .notice-button {
      .notice-highlight {
        color: @mine-shaft;
      }
    }
  }

  .notice-button {
    width: 34px;
    height: 34px;
    position: absolute;
    right: 0px;
    top: 0;
    text-align: center;
    .icon-notice {
      color: @mine-shaft;
    }

    .notice-highlight {
      color: @chalky;
    }
  }

  .extract {
    font-weight: normal;
    display: inline-block;
    font-size: 13px;
    line-height: 1.328571;
  }

  .child-notice-indicator {
    &::before {
      content: '';
      width: 11px;
      height: 11px;
      background: white;
      border: 2px solid @silver-calice;
      position: absolute;
      top: -5px;
      right: -7px;
      border-radius: 50%;
    }

    width: 15px;
    position: absolute;
    top: 0px;
    bottom: 0px;
    background-color: @mercury;
    border-right: 2px solid @silver-calice;

    & + a {
      padding-left: 25px;
    }
  }

  &.notice-unfolded {
    background-color: @white;
    margin-right: -15px;
    border-color: @mine-shaft !important;
    border-style: solid;
    border-width: 1px 0 1px 0px;

    &:not(.child-notice) {
      border-width: 1px 0 1px 4px;
    }

    &:not(.child-notice) > a {
      padding: 9px 35px 9px 6px;
    }

    & > a {
      padding: 9px 35px 9px 25px;
    }

    .notice-button {
      right: 15px;
      .notice-close {
        font-size: 30px;
        color: @chalky;
      }
    }

    .child-notice-indicator {
      &::before {
        border-color: @mine-shaft;
      }
      border-color: @mine-shaft;
      top: -1px;
      bottom: -1px;
    }

    .extract {
      color: @dusty-gray;
    }

    &::after {
      content: '';
      position: absolute;
      top: 22px;
      right: -7px;
      border-top: 7px solid transparent;
      border-left: 7px solid @white;
      border-bottom: 7px solid transparent;
      z-index: 101;
    }
  }

  &:not(.notice-unfolded) {
    .corner-cut {
      width: 0;
      height: 0;
      border-style: solid;
      position: absolute;
      &.top-right {
        top: 0;
        right: 0;
        border-width: 0 12px 12px 0;
        border-color: transparent @mercury transparent transparent;
        opacity: 1;
      }
    }
  }
}
</style>
